import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Button, IconButton, TextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
// import { styled } from '@mui/material/styles';
import { AuthenticationAPI } from '../../services/api/Authentication';
import { StyledCheckbox } from '../../components/component/ThemeComponent';

// const StyledLink = styled(Typography)({
// 	display: 'inline',
// 	cursor: 'pointer',
// 	'&:hover': {
// 		textDecoration: 'underline',
// 	},
// });

const LoginPage: React.FC = () => {
	const navigate = useNavigate();
	const [formValues, setFormValues] = useState({ email: '', password: '' });
	const [formErrors, setFormErrors] = useState({ email: '', password: '' });
	const [rememberMe, setRememberMe] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const validate = () => {
		const errors = { email: '', password: '' };
		const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

		if (!formValues.email) {
			errors.email = 'Email is required';
		} else if (!emailPattern.test(formValues.email)) {
			errors.email = 'Invalid email format';
		}

		if (!formValues.password) {
			errors.password = 'Password is required';
		} else if (formValues.password.length < 8) {
			errors.password = 'Password must be at least 8 characters';
		}

		setFormErrors(errors);
		return Object.values(errors).every((error) => error === '');
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormValues({ ...formValues, [name]: value });
	};

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRememberMe(event.target.checked);
	};

	const handleClickShowPassword = () => {
		setShowPassword((prev) => !prev);
	};

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (validate()) {
			try {
				setLoading(true);
				const res = await AuthenticationAPI.Login(formValues);

				if (res?.isError) {
					console.log('Error logging in user:', res);
					alert(`Login failed: ${res.error.errors}`);
				} else {
					localStorage.setItem('token', res?.data?.data?.accessToken);
					if (rememberMe) {
						localStorage.setItem('token', res?.data?.accessToken);
					} else {
						sessionStorage.setItem('token', res?.data?.token);
					}
					navigate('/');
				}
			} catch (error) {
				console.error('Failed to log in user:', error);
				alert(`Login failed: ${error instanceof Error ? error.message : String(error)}`);
			} finally {
				setLoading(false);
			}
		}
	};

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: { xs: 'center', md: 'flex-end' },
				justifyContent: 'center',
				height: '100vh',
				background: `linear-gradient(160deg,rgba(57, 49, 138, 1) 0%, rgba(131, 29, 141, 1) 50%, rgba(236, 0, 139, 1) 100%)`,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				backgroundAttachment: 'fixed',
			}}
		>
			<Container
				component="main"
				maxWidth="xs"
				sx={{
					padding: '20px',
					borderRadius: '10px',
					backgroundColor: 'white',
					boxShadow: 3,
					overflowY: 'auto',
					maxHeight: '90vh',
				}}
			>
				<Box textAlign="center">
					<img
						src="/assets/login-logo.png"
						alt="Login Logo"
						style={{ width: '300px', height: 'auto' }}
					/>
				</Box>
				{/* <div style={{ textAlign: 'center' }}>
					<StyledLink
						variant="h5"
						fontFamily={'Poppins'}
						fontWeight={700}
						color="#EC008B"
						textAlign="center"
						mb={2}
						onClick={() => navigate('/login')}
					>
						Login
					</StyledLink>
				</div> */}
				<form onSubmit={handleSubmit}>
					<Box mb={2}>
						<label htmlFor="email">Email Id</label>
						<TextField
							size="small"
							id="email"
							name="email"
							type="email"
							value={formValues.email}
							onChange={handleChange}
							placeholder="Email"
							fullWidth
							margin="dense"
							variant="outlined"
						/>
						{formErrors.email && (
							<Typography variant="caption" color="error" sx={{ color: 'red' }}>
								{formErrors.email}
							</Typography>
						)}
					</Box>
					<Box mb={0}>
						<label htmlFor="password">Password</label>
						<TextField
							size="small"
							id="password"
							name="password"
							type={showPassword ? 'text' : 'password'}
							value={formValues.password}
							onChange={handleChange}
							placeholder="Password"
							fullWidth
							margin="dense"
							variant="outlined"
							slotProps={{
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label={
													showPassword
														? 'hide the password'
														: 'display the password'
												}
												onClick={handleClickShowPassword}
												onMouseDown={handleMouseDownPassword}
												edge="end"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									),
								},
							}}
						/>
						{formErrors.password && (
							<Typography variant="caption" color="error" sx={{ color: 'red' }}>
								{formErrors.password}
							</Typography>
						)}
					</Box>
					<Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
						<Box display="flex" alignItems="center">
							<StyledCheckbox
								type="checkbox"
								id="rememberMe"
								checked={rememberMe}
								onChange={handleCheckboxChange}
							/>
							<label
								htmlFor="rememberMe"
								style={{ marginLeft: '2px', color: '#7C7575', fontSize: '12px' }}
							>
								Remember Me
							</label>
						</Box>
					</Box>
					<section style={{ textAlign: 'center' }}>
						<Button
							type="submit"
							variant="contained"
							loading={loading}
							loadingIndicator="Login..."
						>
							Login
						</Button>
					</section>
					{/* <section style={{ textAlign: 'center' }}>
						<a href="/forgot-password">
							<Typography variant="subtitle1">Forgot Password</Typography>
						</a>
					</section> */}
				</form>
			</Container>
		</Box>
	);
};

export default LoginPage;
